
import React from 'react';
import { useState } from 'react';
import { supabase, getAuthRedirectUrl } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { SignUpResult } from '@/context/AuthContext';
import { analytics } from '@/services/analytics/analyticsService';

interface EmailAuthProps {
  setIsLoading: (loading: boolean) => void;
  setUser: (user: any) => void;
  setSession: (session: any) => void;
  setIsAdmin: (isAdmin: boolean) => void;
}

export const useEmailAuth = ({ 
  setIsLoading, 
  setUser, 
  setSession, 
  setIsAdmin 
}: EmailAuthProps) => {
  
  const signIn = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      console.log('Attempting sign in with email:', email);
      
      // Track sign-in attempt
      analytics.trackAuthEvent('sign_in', 'email');
      
      // Simple validation
      if (!email || !password) {
        toast.error('Please enter both email and password');
        setIsLoading(false);
        return false;
      }
      
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        console.error('Sign-in error:', error);
        toast.error(error.message || 'Failed to sign in');
        // Track failed sign-in with error reason
        analytics.trackAuthEvent('sign_in', 'email');
        setIsLoading(false);
        return false;
      }
      
      console.log('Sign-in successful:', data.user?.email);
      
      // Check if we have session and user data before proceeding
      if (!data.session || !data.user) {
        console.error('Sign-in successful but missing session or user data');
        toast.error('Authentication successful but user data is missing');
        setIsLoading(false);
        return false;
      }
      
      // Set auth state directly to avoid waiting for auth state change event
      setUser(data.user);
      setSession(data.session);
      
      // Track successful sign-in
      analytics.trackAuthEvent('sign_in', 'email', data.user.id);
      
      // Set analytics user ID
      analytics.setUserId(data.user.id);
      
      // Ensure loading is set to false before returning
      setIsLoading(false);
      return true; 
    } catch (error: any) {
      console.error('Sign-in error details:', error);
      // Make sure loading is set to false on error
      setIsLoading(false);
      toast.error(error.message || 'Failed to sign in');
      // Track error
      analytics.trackAuthEvent('sign_in', 'email');
      return false;
    }
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string): Promise<SignUpResult> => {
    try {
      setIsLoading(true);
      
      // Track sign-up attempt
      analytics.trackAuthEvent('sign_up', 'email');
      
      // Enhanced logging for signup attempt
      console.log('Starting signup process for', email, {
        firstName,
        lastName
      });
      
      // First check if the email is valid
      if (!email || !email.includes('@')) {
        toast.error('Please enter a valid email address');
        setIsLoading(false);
        return false;
      }
      
      // Set up the redirect URL - use the current window's origin
      const redirectTo = getAuthRedirectUrl('signup');
      
      console.log('Using redirectTo URL for signup:', redirectTo);
      
      // Proceed with signup
      const { data, error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
          },
          emailRedirectTo: redirectTo
        }
      });
      
      if (error) {
        console.error('Sign-up error:', error);
        
        // Track failed signup with error reason
        analytics.trackAuthEvent('sign_up', 'email');
        
        // Handle specific error cases with clearer messages
        if (error.message.includes('already registered')) {
          toast.error('This email is already registered', {
            description: 'Please try signing in instead'
          });
        } else if (error.message.includes('Database error')) {
          // Special handling for database errors which might be due to profile creation issues
          console.log('Database error detected, checking if session was created anyway');
          
          // Try to get session to see if authentication part succeeded
          const { data: sessionData } = await supabase.auth.getSession();
          
          if (sessionData?.session) {
            console.log('Session exists despite database error, attempting to continue');
            
            // Set auth state directly
            setUser(sessionData.session.user);
            setSession(sessionData.session);
            
            toast.warning('Your account was created but there was an issue with your profile', {
              description: 'Some features may be limited until this is resolved'
            });
            
            // Track partial success
            analytics.trackAuthEvent('sign_up', 'email', sessionData.session.user.id);
            
            // Set analytics user ID
            analytics.setUserId(sessionData.session.user.id);
            
            setIsLoading(false);
            return true;
          }
          
          toast.error('Could not create account', {
            description: 'There was a database issue on our end. Please try again later.'
          });
        } else {
          toast.error(error.message || 'Failed to sign up');
        }
        
        setIsLoading(false);
        return false;
      }
      
      console.log('Sign-up response:', data);
      
      if (!data.user) {
        console.error('Sign-up response missing user data');
        toast.error('Failed to create account: No user data returned');
        setIsLoading(false);
        return false;
      }
      
      console.log('Sign-up successful, user created:', data.user?.id);
      
      // Track successful signup
      analytics.trackAuthEvent('sign_up', 'email', data.user.id);
      
      // Check if email confirmation is required
      if (data.session) {
        console.log('User session created immediately, no email confirmation required');
        
        // Set auth state directly
        setUser(data.user);
        setSession(data.session);
        
        // Set analytics user ID
        analytics.setUserId(data.user.id);
        
        toast.success('Account created successfully! You are now logged in.');
        setIsLoading(false);
        return true;
      } else {
        console.log('Email confirmation required, no session created yet');
        
        // Track verification email sent
        analytics.trackEvent({
          category: 'User',
          action: 'verification_email_sent',
          label: email
        });
        
        // Show a clear message to the user about what to expect next
        toast.success('Account created! Please check your email to confirm your account.', {
          duration: 8000,
          description: 'Check your inbox (including spam folder) for a verification email'
        });
        
        setIsLoading(false);
        // Return result object with email confirmation details
        return {
          success: true,
          requiresEmailConfirmation: true,
          userId: data.user.id,
          email: email
        };
      }
    } catch (error: any) {
      console.error('Sign-up error details:', error);
      toast.error(error.message || 'Failed to sign up');
      
      // Track error
      analytics.trackAuthEvent('sign_up', 'email');
      
      setIsLoading(false);
      return false;
    }
  };

  return { signIn, signUp };
};
